import * as Yup from "yup";
import { toMinutes } from "./utils";

export const loginSchema = (t) => {
  return Yup.object({
    email: Yup.string()
      .email(t("Invalid email"))
      .required(t("Email is required")),
    password: Yup.string().required(t("Password is required")),
    remember: Yup.boolean(),
  });
};

export const getUserValidationSchema = (t, isEdit) => {
  return Yup.object({
    name: Yup.string()
      .required(t("Name is required"))
      // At least one alphabet
      .matches(/[a-zA-Z]/, t("Name must contain at least one letter"))
      // Allow letters, numbers, spaces, dot, apostrophe, hyphen
      .matches(/^[a-zA-Z0-9\s.'-]+$/, t("Name contains invalid characters")),

    email: Yup.string()
      .email(t("Invalid email"))
      .required(t("Email is required")),
    phoneNumber: Yup.string()
      .required(t("Phone number is required"))
      .matches(
        /^(\d{9}|\+\d{1,3}\d{9})$/,
        t("Enter 9-digit phone number or include a valid country code"),
      ),

    // roleId: Yup.string().required(t("Role is required")),
    accountType: Yup.string().required(t("Account type is required")),
    // branches: Yup.array().min(1, t("Select at least one branch")),
    roleId: Yup.string().when("accountType", {
      is: "admin",
      then: (schema) =>
        schema.required(
          t("Role is Required"),
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
  });
};
export const getVerifyInviteValidationSchema = (t) => {
  return Yup.object({
    password: Yup.string()
      .min(6, t("Password must be at least 6 characters"))
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[@$!%*?&])[A-Za-z@$!%*?&]/,
        t(
          "Password must contain at least one uppercase letter, one lowercase letter, and one special character",
        ),
      )
      .required(t("Password is required")),

    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password")], t("Passwords do not match"))
      .required(t("Confirm password is required")),
  });
};

export const getAddonValidationSchema = (t, isEdit) => {
  return Yup.object({
    title: Yup.object({
      en: Yup.string().required(t("Title (English) is required")),
      ar: Yup.string().required(t("Title (Arabic) is required")),
    }).required(t("Title is required")),

    description: Yup.object({
      en: Yup.string().required(t("Description (English) is required")),
      ar: Yup.string().required(t("Description (Arabic) is required")),
    }).required(t("Title is required")),
    price: Yup.number()
      .typeError(t("Price must be a number"))
      .required(t("Price is required")),

    currency: Yup.string().required(t("Currency is required")),
    allowedFor: Yup.array().min(1, t("Select at least one plan")),
    isUnlimited: Yup.boolean(),
    maxUses: Yup.number()
      .typeError(t("Max uses must be a number"))
      .min(0, t("Max uses cannot be negative"))
      .nullable(),
  });
};

export const getVehicleMakeValidationSchema = (t, isEdit) => {
  return Yup.object({
    name: Yup.object({
      en: Yup.string().required(t("Name EN is required")),
      ar: Yup.string().required(t("Name AR is required")),
    }).required(t("Name is required")),
  });
};

export const getVehicleModelValidationSchema = (t, isEdit) => {
  return Yup.object({
    makeId: Yup.string().required(t("Make is required")),
    name: Yup.object({
      en: Yup.string().required(t("Name EN is required")),
      ar: Yup.string().required(t("Name AR is required")),
    }).required(t("Name is required")),
  });
};

export const getMembershipPlanValidationSchema = (t, isEdit) => {
  return Yup.object({
    planType: Yup.string().required(t("Plan type is required")),

    title: Yup.object({
      en: Yup.string().required(t("Title (English) is required")),
      ar: Yup.string().required(t("Title (Arabic) is required")),
    }).required(),

    subTitle: Yup.object({
      en: Yup.string().required(t("Highlight Title (English) is required")),
      ar: Yup.string().required(t("Highlight Title (Arabic) is required")),
    }).required(),

    // description: Yup.object({
    //   en: Yup.string().required(t("Description (English) is required")),
    //   ar: Yup.string().required(t("Description (Arabic) is required")),
    // }).required(),

    pricingOptions: Yup.array()
      .of(
        Yup.object({
          label: Yup.string().required(t("Pricing label is required")),
          billingCycle: Yup.string().required(t("Billing cycle is required")),
          price: Yup.number()
            .typeError(t("Price must be a number"))
            .required(t("Price is required"))
            .min(0, t("Price cannot be negative")),
          discountInPercentage: Yup.number()
            .typeError(t("Discount Amount must be a number"))
            .min(0, t("Percentage cannot be negative"))
            .max(100, t("Percentage cannot be greater then 100")),
          currency: Yup.string().required(t("Currency is required")),
          isUnlimited: Yup.boolean(),
          isPopular: Yup.boolean(),
        }),
      )
      .min(1, t("At least one pricing option is required"))
      .required(t("Pricing options are required")),

    addonEnabled: Yup.boolean(),

    addonIncluded: Yup.boolean().when("addonEnabled", {
      is: true,
      then: (schema) =>
        schema.required(
          t("Addon included is required when addons are enabled"),
        ),
      otherwise: (schema) => schema.notRequired(),
    }),

    addonsAllowed: Yup.array().when("addonEnabled", {
      is: true,
      then: (schema) => schema.min(1, t("Select at least one addon")),
      otherwise: (schema) => schema.notRequired(),
    }),

    features: Yup.array()
      .of(
        Yup.object({
          title: Yup.object({
            en: Yup.string().required(t("Feature (English) is required")),
            ar: Yup.string().required(t("Feature (Arabic) is required")),
          }).required(),
        }),
      )
      .min(1, t("At least one feature is required"))
      .required(t("Features are required")),

    isRecommended: Yup.boolean(),

    sortOrder: isEdit
      ? Yup.number()
          .typeError(t("Sort order must be a number"))
          .min(0, t("Sort order must be greater than 0"))
          .notRequired()
      : Yup.mixed().notRequired(),

    // iconUrl: isEdit
    //   ? Yup.mixed().notRequired()
    //   : Yup.mixed().required(t("Icon is required")),
  });
};

export const getFreeWashRulesValidationSchema = (t) => {
  return Yup.object({
    status: Yup.boolean(),

    ruleType: Yup.string().when("status", {
      is: true,
      then: (schema) => schema.required(t("Type is required")),
      otherwise: (schema) => schema.notRequired(),
    }),

    totalFreeWashes: Yup.number()
      .typeError(t("Total Free Washes must be a number"))
      .when(["status", "ruleType"], {
        is: (status, ruleType) => status === true && ruleType === "PER_VEHICLE",
        then: (schema) =>
          schema
            .required(t("Total Free Washes is required"))
            .min(1, t("Total Free Washes must be greater than 0")),
        otherwise: (schema) => schema.notRequired(),
      }),
      planId :Yup.string().required("Plan is required")
  });
};

export const getVehicleColorValidationSchema = (t, isEdit) => {
  return Yup.object({
    name: Yup.object({
      en: Yup.string().trim().required(t("Color name (EN) is required")),
      ar: Yup.string().trim().required(t("Color name (AR) is required")),
    }).required(t("Color name is required")),

    code: Yup.string()
      .matches(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, t("Invalid color code"))
      .required(t("Color code is required")),
  });
};
export const getNotificationValidationSchema = (t) =>
  Yup.object({
    title: Yup.object({
      en: Yup.string().required(t("Title (English) is required")),
      ar: Yup.string().required(t("Title (Arabic) is required")),
    }).required(),

    body: Yup.object({
      en: Yup.string().required(t("Description (English) is required")),
      ar: Yup.string().required(t("Description (Arabic) is required")),
    }).required(),

    type: Yup.string()
      .oneOf(["all", "specific_customers"])
      .required(t("Send for is required")),

    customers: Yup.array().when("type", {
      is: "specific_customers",
      then: (schema) =>
        schema
          .min(1, t("Please select at least one customer"))
          .required(t("Customers are required")),
      otherwise: (schema) => schema.notRequired(),
    }),
    isScheduled: Yup.boolean(),

    scheduledTime: Yup.mixed().when("isScheduled", {
      is: true,
      then: (schema) =>
        schema.required(t("Scheduled date & time is required")).nullable(),
      otherwise: (schema) => schema.nullable(),
    }),
  });

export const getBranchValidationSchema = (t, isEdit) =>
  Yup.object({
    name: Yup.object({
      en: Yup.string().required(t("Name (English) is required")),
      ar: Yup.string().required(t("Name (Arabic) is required")),
    }),

    address: Yup.object({
      en: Yup.string().required(t("Address (English) is required")),
      ar: Yup.string().required(t("Address (Arabic) is required")),
    }),

    latitude: Yup.number()
      .typeError(t("Latitude must be a number"))
      .required(t("Latitude is required")),

    longitude: Yup.number()
      .typeError(t("Longitude must be a number"))
      .required(t("Longitude is required")),
    openingHours: Yup.array().when("open24Hours", {
      is: false,
      then: (schema) =>
        schema
          .of(
            Yup.object({
              day: Yup.string().required(t("Day is required")),
              isClosed: Yup.boolean(),

              openTime: Yup.mixed().when("isClosed", {
                is: false,
                then: (s) => s.required(t("Open time is required")),
              }),

              closeTime: Yup.mixed().when("isClosed", {
                is: false,
                then: (s) =>
                  s
                    .required(t("Close time is required"))
                    .test(
                      "end-after-start",
                      t("Close time must be greater than open time"),
                      function (closeTime) {
                        const { openTime } = this.parent;
                        if (!openTime || !closeTime) return true;
                        return toMinutes(closeTime) > toMinutes(openTime);
                      },
                    ),
              }),
            }),
          )
          .test("unique-days", t("Day already selected"), function (rows) {
            if (!rows) return true;

            const seen = new Set();

            for (let i = 0; i < rows.length; i++) {
              const day = rows[i]?.day;
              if (!day) continue;

              if (seen.has(day)) {
                return this.createError({
                  path: `openingHours.${i}.day`,
                  message: t("Day already selected"),
                });
              }
              seen.add(day);
            }
            return true;
          }),
      otherwise: (schema) => schema.notRequired(),
    }),
    images: Yup.array().test(
      "required",
      t("At least one image is required"),
      (value) => value && value.length > 0,
    ),
    whatsappNumber: Yup.string().matches(
      /^(\d{9}|\+\d{1,3}\d{9})$/,
      t("Enter 9-digit phone number or include a valid country code"),
    ),
    googleMapLink: Yup.string()
  .matches(/^https?:\/\/.+/, "URL must start with http:// or https://")
  .nullable(),
  });
export const forgotPasswordSchema = (t) => {
  return Yup.object({
    email: Yup.string().email(t("Invalid email")).required("Email is required"),
  });
};
export const verifyOTPForgotPasswordSchema = (t) => {
  return Yup.object({
otp: Yup.string()
  .matches(/^[0-9]+$/, t("OTP must contain only numbers"))
  .max(5, t("OTP must be at most 5 digits"))
  .required(t("OTP is required")),  });
};
export const getForgottenResetValidationSchema = (t) => {
  return Yup.object({
    newPassword: Yup.string()
      .min(6, t("Password must be at least 6 characters"))
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[@$!%*?&])[A-Za-z@$!%*?&]/,
        t(
          "Password must contain at least one uppercase letter, one lowercase letter, and one special character",
        ),
      )
      .required(t("Password is required")),

    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newPassword")], t("Passwords do not match"))
      .required(t("Confirm password is required")),
  });
};

export const getBannerValidationSchema = (t) =>
  Yup.object({
    // title: Yup.object({
    //   en: Yup.string().required(t("Name (English) is required")),
    //   ar: Yup.string().required(t("Name (Arabic) is required")),
    // }),

    // description: Yup.object({
    //   en: Yup.string().required(t("Address (English) is required")),
    //   ar: Yup.string().required(t("Address (Arabic) is required")),
    // }),
    image: Yup.string().required(t("Image is required")),
  });

export const getNotificationRuleValidationSchema = (
  t,
  isScheduleTime = false,
) =>
  Yup.object({
    name: Yup.string().required(t("Rule name is required")),

    category: Yup.string()
      .oneOf(
        ["USER", "WASH", "MEMBERSHIP", "ENGAGEMENT", "STORE"],
        t("Invalid category"),
      )
      .required(t("Category is required")),

    audience: Yup.object({
      type: Yup.string()
        .oneOf(["ALL", "SEGMENT", "CUSTOM"], t("Invalid audience type"))
        .required(t("Audience type is required")),

      // Segments validation - only for SEGMENT type
      segments: Yup.array().when("type", {
        is: "SEGMENT",
        then: (schema) =>
          schema
            .of(
              Yup.object({
                type: Yup.string().required(t("Segment type is required")),
                config: Yup.object().notRequired(),
              }),
            )
            .min(1, t("Please select at least one segment"))
            .test(
              "has-valid-segment",
              t("Please select a segment"),
              (segments) => {
                return segments && segments.length > 0 && segments[0]?.type;
              },
            )
            .required(t("Segments are required")),
        otherwise: (schema) => schema.notRequired(),
      }),

      // Conditions validation - only for CUSTOM type
      conditions: Yup.array().when("type", {
        is: "CUSTOM",
        then: (schema) =>
          schema
            .of(
              Yup.object({
                event: Yup.string().required(t("Event is required")),
                operator: Yup.string().required(t("Operator is required")),
                value: Yup.mixed().required(t("Value is required")),
                type: Yup.string().required(t("Type is required")),
              }),
            )
            .min(1, t("At least one condition is required"))
            .test(
              "unique-events",
              t("Each event can only be selected once"),
              (conditions) => {
                if (!conditions || conditions.length === 0) return true;

                // Get all event values
                const events = conditions
                  .map((cond) => cond.event)
                  .filter(Boolean);

                // Check if there are duplicates by comparing array length with Set size
                const uniqueEvents = new Set(events);
                return events.length === uniqueEvents.size;
              },
            )
            .required(t("Conditions are required")),
        otherwise: (schema) => schema.notRequired(),
      }),

      // Logical operator validation - only for CUSTOM type
      logicalOperator: Yup.string().when("type", {
        is: "CUSTOM",
        then: (schema) =>
          schema
            .oneOf(["AND", "OR"], t("Invalid logical operator"))
            .required(t("Logical operator is required")),
        otherwise: (schema) => schema.notRequired(),
      }),
    }).required(),

    content: Yup.object({
      title: Yup.object({
        en: Yup.string().required(t("Title (English) is required")),
        ar: Yup.string().required(t("Title (Arabic) is required")),
      }).required(),

      body: Yup.object({
        en: Yup.string().required(t("Description (English) is required")),
        ar: Yup.string().required(t("Description (Arabic) is required")),
      }).required(),
    }).required(),

    notificationTime: isScheduleTime
      ? Yup.string().required(t("Notification time is required"))
      : Yup.string().notRequired(),
  });
export const changePasswordSchema = (t) => {
  return Yup.object({
    currentPassword: Yup.string().required("Current Password is required"),
    newPassword: Yup.string()
      .min(6, t("Password must be at least 6 characters"))
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[@$!%*?&])[A-Za-z@$!%*?&]/,
        t(
          "Password must contain at least one uppercase letter, one lowercase letter, and one special character",
        ),
      )
      .required(t("Password is required")),

    confirmNewPassword: Yup.string()
      .oneOf([Yup.ref("newPassword")], t("Passwords do not match"))
      .required(t("Confirm password is required")),
  });
};
export const changeAdminUserPasswordSchema = (t) => {
  return Yup.object({
    newPassword: Yup.string()
      .min(6, t("Password must be at least 6 characters"))
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[@$!%*?&])[A-Za-z@$!%*?&]/,
        t(
          "Password must contain at least one uppercase letter, one lowercase letter, and one special character",
        ),
      )
      .required(t("Password is required")),

    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newPassword")], t("Passwords do not match"))
      .required(t("Confirm password is required")),
  });
};
export const editProfileSchema = (t) => {
  return Yup.object({
    name: Yup.string()
      .required(t("Name is required"))
      // At least one alphabet
      .matches(/[a-zA-Z]/, t("Name must contain at least one letter"))
      // Allow letters, numbers, spaces, dot, apostrophe, hyphen
      .matches(/^[a-zA-Z0-9\s.'-]+$/, t("Name contains invalid characters")),

    phoneNumber: Yup.string()
      .required(t("Phone number is required"))
      .matches(
        /^(\d{9}|\+\d{1,3}\d{9})$/,
        t("Enter 9-digit phone number or include a valid country code"),
      ),
  });
};
export const AddCouponSchema = (t) => {
  return Yup.object({
    couponCode: Yup.string().required(t("Code is required")),
    discountType: Yup.string().required(t("Discount Type is required")),
    discountAmount: Yup.number()
      .required(t("Discount amount is required"))
      .positive(t("Discount amount must be positive")),

    usageRules: Yup.object({
      hasGlobalUsageLimit: Yup.boolean(),
      maxTotalRedemptions: Yup.number().when("hasGlobalUsageLimit", {
        is: true,
        then: (schema) =>
          schema
            .required(t("Max total redemptions is required"))
            .positive(t("Must be a positive number"))
            .integer(t("Must be a whole number")),
        otherwise: (schema) => schema.nullable(),
      }),

      hasPerUserUsageLimit: Yup.boolean(),
      maxRedemptionsPerUser: Yup.number().when("hasPerUserUsageLimit", {
        is: true,
        then: (schema) =>
          schema
            .required(t("Max redemptions per user is required"))
            .positive(t("Must be a positive number"))
            .integer(t("Must be a whole number")),
        otherwise: (schema) => schema.nullable(),
      }),

      hasMaxDiscountLimit: Yup.boolean(),
      maxDiscountAmount: Yup.number().when("hasMaxDiscountLimit", {
        is: true,
        then: (schema) =>
          schema
            .required(t("Max discount amount is required"))
            .positive(t("Must be a positive number")),
        otherwise: (schema) => schema.nullable(),
      }),
    }),

    neverEnds: Yup.boolean(),
    startsAt: Yup.date().required(t("Start date is required")),
    endsAt: Yup.date()
      .when("neverEnds", ([neverEnds], schema) => {
        return neverEnds === false
          ? schema.required(t("End date is required"))
          : schema.nullable();
      })
      .when(["neverEnds", "startsAt"], ([neverEnds, startsAt], schema) => {
        return neverEnds === false && startsAt
          ? schema.min(startsAt, t("End date must be greater than start date"))
          : schema;
      }),

    allowedUsers: Yup.array(),
  });
};
export const updateMpinSchema = (t) => {
  return Yup.object({
    mpin: Yup.string().min(4).max(4)
      .required(t("Mpin is required")),
  });
};
export const storeCameraValidationSchema = (t, isEdit) => {
  return Yup.object({
    branchId: Yup.string().required(t("Store is required")),
    name: Yup.string().required(t("Name is required")),
    cameraId: Yup.string().required(t("Camera / ANPR device id is required")),
    role: Yup.string().required(t("Camera / ANPR device role is required")),
  });
};